import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getCampaigns, getPrograms, getRuns } from "../api/client.js";
import { Icon } from "../components/Icon.jsx";
import { Shell } from "../components/Shell.jsx";
import {
  ErrorCard,
  EvidencePeriodHeader,
  Loading,
  MetricValue,
  Panel,
  StatusPill,
} from "../components/ui.jsx";
import { DOMAIN_LABELS } from "../domainModel.js";
import { DECISION_CATEGORIES, highPriorityTieBreaker, rankDecisionQueue } from "../decisionQueuePolicy.js";
import { METRIC_GRAINS, programMetric } from "../metricGrainContract.js";
import { ACTIVATION_PATHS, campaignRoute, opportunitiesRoute } from "../navConfig.js";

const WORKFLOW_STAGES = [
  { label: "Identify", detail: "Review aggregate opportunities" },
  { label: "Create", detail: "Define audience and outreach" },
  { label: "Govern", detail: "Check safety and approve" },
  { label: "Publish", detail: "Release the approved definition" },
  { label: "Measure", detail: "Assess evidence when available" },
];

function hasRole(auth, ...roles) {
  const assigned = new Set(auth?.roles || []);
  return roles.some((role) => assigned.has(role));
}

function plainDate(value) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unavailable";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(date);
}

function campaignDecisions(campaigns, auth) {
  const canManage = hasRole(auth, "hr_manager", "tenant_admin");
  const canReview = hasRole(auth, "campaign_reviewer", "tenant_admin");
  const canPublish = hasRole(auth, "campaign_operator", "tenant_admin");

  return campaigns.flatMap((campaign) => {
    const common = {
      key: `campaign-${campaign.id}`,
      kind: "Campaign",
      title: campaign.name,
      program: DOMAIN_LABELS[campaign.programDomain] || campaign.programDomain,
      evidencePeriod: campaign.runMonth,
      audience: campaign.cohortEstimate?.meetsMinimum ? campaign.cohortEstimate.memberCount : null,
      audienceGrain: METRIC_GRAINS.COVERED_PEOPLE,
      updatedLabel: `Updated ${plainDate(campaign.updatedAt)}`,
      href: campaignRoute(campaign.id),
    };

    if (
      campaign.governance?.overall === "fail" &&
      !["deployed", "completed", "cancelled"].includes(campaign.workflowStatus) &&
      (campaign.ownerId === auth?.userId || hasRole(auth, "tenant_admin"))
    ) {
      return [
        {
          ...common,
          category: DECISION_CATEGORIES.GOVERNANCE_BLOCKED,
          status: "fail",
          statusLabel: "Governance blocked",
          summary: "A blocking governance issue requires correction before this campaign can progress.",
          actionLabel: "Resolve blocker",
        },
      ];
    }

    if (campaign.workflowStatus === "ready_for_review" && canReview && campaign.ownerId !== auth?.userId) {
      return [
        {
          ...common,
          category: DECISION_CATEGORIES.GOVERNANCE_REVIEW,
          status: "ready_for_review",
          statusLabel: "Review required",
          summary: "The campaign is waiting for an independent maker-checker decision.",
          actionLabel: "Review campaign",
        },
      ];
    }

    if (campaign.workflowStatus === "approved" && canPublish) {
      return [
        {
          ...common,
          category: DECISION_CATEGORIES.PUBLICATION_DECISION,
          status: "approved",
          statusLabel: "Publication decision",
          summary: "Governance approval is complete; publication remains a separate operator decision.",
          actionLabel: "Review publication",
        },
      ];
    }

    if (campaign.workflowStatus === "draft" && canManage && campaign.ownerId === auth?.userId) {
      return [
        {
          ...common,
          category: DECISION_CATEGORIES.DRAFT_ATTENTION,
          status: "draft",
          statusLabel: "Draft needs attention",
          summary: "Campaign setup is incomplete and has not been submitted for independent review.",
          actionLabel: "Open draft",
        },
      ];
    }

    return [];
  });
}

function opportunityDecisions(programs) {
  return programs
    .filter((program) => program.opportunityCount > 0 || program.opportunitiesOnNotEnrolledCount > 0)
    .filter((program) => program.latestRun?.qualityGate?.status !== "fail")
    .map((program) => {
      const hasQualityWarning = program.latestRun?.qualityGate?.status === "warn";
      const highPriorityCount = program.highPriorityCount || 0;
      const audienceMetric = programMetric(program, "opportunityCount");
      return {
        key: `opportunity-${program.id}`,
        kind: "Opportunity",
        title: `${DOMAIN_LABELS[program.programDomain] || program.name} opportunities`,
        program: DOMAIN_LABELS[program.programDomain] || program.name,
        evidencePeriod: program.latestRun?.runMonth,
        audience: audienceMetric.value,
        audienceGrain: audienceMetric.grain,
        updatedLabel: `Evidence period ${program.latestRun?.runMonth || "Unavailable"}`,
        href: opportunitiesRoute({
          programDomain: program.programDomain,
          runMonth: program.latestRun?.runMonth,
        }),
        category: hasQualityWarning
          ? DECISION_CATEGORIES.OPPORTUNITY_QUALITY_WARNING
          : DECISION_CATEGORIES.OPPORTUNITY_READY,
        secondaryPriority: highPriorityTieBreaker(highPriorityCount),
        status: hasQualityWarning ? "warn" : "pass",
        statusLabel: hasQualityWarning ? "Quality warning" : "Ready to review",
        summary: hasQualityWarning
          ? `${program.latestRun.qualityGate.warnCount} data-quality warning must be considered before campaign creation.`
          : `${highPriorityCount.toLocaleString()} high-priority signals are ready for aggregate review.`,
        actionLabel: "View opportunities",
      };
    });
}

export function ActivationEngineOverview({ tenant, auth }) {
  const [programs, setPrograms] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [programRuns, setProgramRuns] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([getPrograms(), getCampaigns()])
      .then(async ([programData, campaignData]) => {
        const loadedPrograms = programData.programs || [];
        const runsByDomain = Object.fromEntries(
          await Promise.all(
            loadedPrograms.map(async (program) => [
              program.programDomain,
              (await getRuns(program.programDomain)).runs || [],
            ])
          )
        );
        setPrograms(loadedPrograms);
        setCampaigns(campaignData.campaigns || []);
        setProgramRuns(runsByDomain);
      })
      .catch(setError);
  }, []);

  const decisions = useMemo(() => {
    if (!programs || !campaigns) return [];
    return rankDecisionQueue([...campaignDecisions(campaigns, auth), ...opportunityDecisions(programs)]);
  }, [auth, campaigns, programs]);

  const latestEvidencePeriod = useMemo(
    () =>
      (programs || [])
        .map((program) => program.latestRun?.runMonth)
        .filter(Boolean)
        .sort()
        .at(-1),
    [programs]
  );
  const latestRefreshDate = useMemo(
    () =>
      Object.values(programRuns || {})
        .flat()
        .filter((run) => run.runMonth === latestEvidencePeriod)
        .map((run) => run.completedAt || run.asOfDate)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .at(-1),
    [latestEvidencePeriod, programRuns]
  );
  const firstOpportunity = decisions.find((decision) => decision.kind === "Opportunity");
  const reviewOpportunitiesHref = firstOpportunity?.href || ACTIVATION_PATHS.opportunities;

  const body = () => {
    if (error) return <ErrorCard error={error} />;
    if (!programs || !campaigns || !programRuns) return <Loading />;

    return (
      <>
        <EvidencePeriodHeader
          period={latestEvidencePeriod}
          refreshedAt={latestRefreshDate}
          freshness={latestRefreshDate ? "current" : "unknown"}
          freshnessLabel={latestRefreshDate ? "Latest available" : undefined}
        />

        <Panel
          full
          headingLevel={2}
          title="Decisions requiring attention"
          desc="Ranked by safety, governance state, workflow readiness, and opportunity priority"
          headerRight={<span className="decision-queue-count">{decisions.length} shown</span>}
        >
          {decisions.length > 0 ? (
            <ol className="decision-queue">
              {decisions.map((decision, index) => (
                <li className={`decision-queue-item ${decision.status}`} key={decision.key}>
                  <span className="decision-rank" aria-label={`Priority ${index + 1}`}>
                    {index + 1}
                  </span>
                  <div className="decision-copy">
                    <div className="decision-heading">
                      <span className="decision-kind">{decision.kind}</span>
                      <StatusPill status={decision.status} label={decision.statusLabel} />
                    </div>
                    <h3>{decision.title}</h3>
                    <p>{decision.summary}</p>
                    <div className="decision-context">
                      <span>{decision.program}</span>
                      <span>{decision.updatedLabel}</span>
                    </div>
                  </div>
                  <div className="decision-audience">
                    <span>Aggregate audience</span>
                    <MetricValue
                      value={decision.audience}
                      grain={decision.audienceGrain}
                      unavailableReason="Audience estimate is not available"
                    />
                  </div>
                  <Link className="decision-action" to={decision.href}>
                    {decision.actionLabel}
                    <Icon name="chev-right" size={14} strokeWidth={2.2} />
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <div className="decision-queue-empty" role="status">
              <Icon name="check" size={18} strokeWidth={2.2} />
              <div>
                <strong>No decisions need attention</strong>
                <span>There are no actionable campaigns or aggregate opportunities in the current data.</span>
              </div>
            </div>
          )}
        </Panel>

        <section className="overview-workflow" aria-labelledby="overview-workflow-title">
          <div className="overview-workflow-heading">
            <h2 id="overview-workflow-title">Activate workflow</h2>
            <p>Each state is distinct; approval does not publish or deliver a campaign.</p>
          </div>
          <ol>
            {WORKFLOW_STAGES.map((stage, index) => (
              <li key={stage.label}>
                <span>{index + 1}</span>
                <div>
                  <strong>{stage.label}</strong>
                  <small>{stage.detail}</small>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </>
    );
  };

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="overview"
      title="Activate overview"
      subtitle="Prioritized aggregate opportunities and campaigns that need a decision"
      headerRight={
        <Link className="bi-btn-primary" to={reviewOpportunitiesHref}>
          Review opportunities <Icon name="chev-right" size={14} strokeWidth={2.2} />
        </Link>
      }
    >
      {body()}
    </Shell>
  );
}
